import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Edit,
  User,
  Calendar,
  Mail,
  Phone,
  Clock,
  Shield,
  Tag,
  Database,
  Users as UsersIcon,
  CheckCircle,
  XCircle,
  RefreshCw,
  Image as ImageIcon,
  FileText,
  Video,
  Music,
  Download,
  Eye,
  Trash,
  ExternalLink,
  ChevronDown,
  ChevronUp,
  UserPlus,
  Share2
} from 'lucide-react';
import AdminApi from '../../apis/adminApis';
import MediaViewModal from './modals/MediaViewModal';
import MediaDeleteConfirmModal from './modals/MediaDeleteConfirmModal';
import UserStatusModal from './modals/UserStatusModal';
import UserRoleModal from './modals/UserRoleModal';
import UserDeleteConfirmModal from './modals/UserDeleteConfirmModal';

// Add Referral interface
interface Referral {
  id: number;
  first_name: string;
  last_name: string;
  email: string;
  created_at: string;
  status: string;
  userProfile?: {
    image_url?: string;
  };
}

interface User {
  id: number;
  first_name: string;
  last_name: string;
  email: string;
  phone_number?: string;
  role: string;
  status: string;
  created_at: string;
  last_login?: string;
  max_upload_limit_mb: number;
  no_of_recipients: number;
  referral_code?: string;
  userProfile?: {
    id: number;
    bio?: string;
    temporaryUrl?: string;
  };
  plan?: {
    id: number;
    plan_id: number;
    subscription_id: string;
    is_active: boolean;
    expiration_date: string;
  };
}

interface Media {
    id: number;
    file_name: string;
    file_type: string;
    file_size: number;
    path: string;
    description?: string;
    is_legacy: number;
    created_at: string;
    user?: { 
      id: number;
      first_name: string;
      last_name: string;
      email: string;
    };
    temporaryUrl?: string;
  }

interface Recipient {
  id: number;
  first_name: string;
  last_name: string;
  email: string;
  phone?: string;
  group: {
    id: number;
    name: string;
  };
  temporaryUrl?: string;
}

interface Transaction {
  id: number;
  amount: number;
  currency: string;
  status: string;
  type: string;
  description?: string;
  created_at: string;
}

interface ActivityLog {
  type: string;
  ip_address: string;
  device: string;
  timestamp: string;
}

const UserDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const userId = parseInt(id || '0');
  
  // State
  const [user, setUser] = useState<User | null>(null);
  const [media, setMedia] = useState<Media[]>([]);
  const [recipients, setRecipients] = useState<Recipient[]>([]);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [activityLogs, setActivityLogs] = useState<ActivityLog[]>([]);
  const [referrals, setReferrals] = useState<Referral[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  
  // UI State
  const [mediaExpanded, setMediaExpanded] = useState<boolean>(true);
  const [recipientsExpanded, setRecipientsExpanded] = useState<boolean>(true);
  const [transactionsExpanded, setTransactionsExpanded] = useState<boolean>(false);
  const [activityExpanded, setActivityExpanded] = useState<boolean>(false);
  const [referralsExpanded, setReferralsExpanded] = useState<boolean>(true);
  
  // Modals
  const [roleModalOpen, setRoleModalOpen] = useState<boolean>(false);
  const [statusModalOpen, setStatusModalOpen] = useState<boolean>(false);
  const [deleteUserModalOpen, setDeleteUserModalOpen] = useState<boolean>(false);
  const [selectedMedia, setSelectedMedia] = useState<Media | null>(null);
  const [viewMediaModalOpen, setViewMediaModalOpen] = useState<boolean>(false);
  const [deleteMediaModalOpen, setDeleteMediaModalOpen] = useState<boolean>(false);
  
  // Fetch user data
  const fetchUserData = async () => {
    setLoading(true);
    try {
      const response = await AdminApi.getUser(userId);
      
      if (response.data.status === 'success') {
        setUser(response.data.data);
      } else {
        setError('Failed to load user data');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error loading user data');
      console.error('Error fetching user:', err);
    } finally {
      setLoading(false);
    }
  };
  
  // Fetch user media
  const fetchUserMedia = async () => {
    try {
      const response = await AdminApi.getUserMedia(userId);
      
      if (response.data.status === 'success') {
        setMedia(response.data.data.data);
      }
    } catch (err) {
      console.error('Error fetching user media:', err);
    }
  };
  
  // Fetch user recipients
  const fetchUserRecipients = async () => {
    try {
      const response = await AdminApi.getUserRecipients(userId);
      
      if (response.data.status === 'success') {
        setRecipients(response.data.data.data);
      }
    } catch (err) {
      console.error('Error fetching user recipients:', err);
    }
  };
  
  // Fetch user transactions
  const fetchUserTransactions = async () => {
    try {
      const response = await AdminApi.getUserTransactions(userId);
      
      if (response.data.status === 'success') {
        setTransactions(response.data.data.data);
      }
    } catch (err) {
      console.error('Error fetching user transactions:', err);
    }
  };
  
  // Fetch user activity
  const fetchUserActivity = async () => {
    try {
      const response = await AdminApi.getUserActivity(userId);
      
      if (response.data.status === 'success') {
        setActivityLogs(response.data.data.activity);
      }
    } catch (err) {
      console.error('Error fetching user activity:', err);
    }
  };
  
  // Fetch user referrals
  const fetchUserReferrals = async () => {
    try {
      const response = await AdminApi.getUserReferrals(userId);
      
      if (response.data.status === 'success') {
        setReferrals(response.data.data || []);
      }
    } catch (err) {
      console.error('Error fetching user referrals:', err);
    }
  };
  
  // Initial data load
  useEffect(() => {
    if (userId) {
      fetchUserData();
      fetchUserMedia();
      fetchUserRecipients();
      fetchUserTransactions();
      fetchUserActivity();
      fetchUserReferrals();
    }
  }, [userId]);
  
  // Handle delete user
  const handleDeleteUser = async () => {
    try {
      const response = await AdminApi.deleteUser(userId);
      
      if (response.data.status === 'success') {
        navigate('/admin/users');
      } else {
        setError('Failed to delete user');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error deleting user');
      console.error('Error deleting user:', err);
    }
  };
  
  // Handle change user role
  const handleChangeRole = async (role: string) => {
    try {
      const response = await AdminApi.changeUserRole(userId, role);
      
      if (response.data.status === 'success') {
        setUser(prev => {
          if (!prev) return null;
          return { ...prev, role };
        });
        setRoleModalOpen(false);
      } else {
        setError('Failed to update user role');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error updating user role');
      console.error('Error changing role:', err);
    }
  };
  
  // Handle change user status
  const handleToggleStatus = async () => {
    try {
      const response = await AdminApi.changeUserStatus(userId);
      
      if (response.data.status === 'success') {
        setUser(prev => {
          if (!prev) return null;
          return { 
            ...prev, 
            status: prev.status === 'active' ? 'inactive' : 'active' 
          };
        });
        setStatusModalOpen(false);
      } else {
        setError('Failed to update user status');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error updating user status');
      console.error('Error changing status:', err);
    }
  };
  
  // Handle delete media
  const handleDeleteMedia = async (mediaId: number) => {
    try {
      const response = await AdminApi.deleteMedia(mediaId);
      
      if (response.data.status === 'success') {
        setMedia(media.filter(item => item.id !== mediaId));
        setDeleteMediaModalOpen(false);
      } else {
        setError('Failed to delete media');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error deleting media');
      console.error('Error deleting media:', err);
    }
  };
  
  // Format date
  const formatDate = (dateString?: string): string => {
    if (!dateString) return 'Never';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };
  
  // Format file size
  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return bytes?.toFixed(2) + ' MB';
    else if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(2) + ' GB';
    else if (bytes < 1024 * 1024 * 1024) return (bytes / (1024 * 1024)).toFixed(2) + ' TB';
    else return (bytes / (1024 * 1024 * 1024)).toFixed(2) + ' TB';
  };
  
  // Format amount
  const formatAmount = (amount: number, currency: string): string => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency
    }).format(amount);
  };
  
  // Get media icon
  const getMediaIcon = (fileType: string) => {
    switch (fileType) {
      case 'image':
        return <ImageIcon className="h-6 w-6 text-blue-500" />;
      case 'video':
        return <Video className="h-6 w-6 text-purple-500" />;
      case 'audio':
        return <Music className="h-6 w-6 text-orange-500" />;
      case 'document':
      default:
        return <FileText className="h-6 w-6 text-green-500" />;
    }
  };

  // Loading state
  if (loading && !user) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex justify-center items-center h-64">
          <RefreshCw className="h-8 w-8 text-gray-400 animate-spin" />
        </div>
      </div>
    );
  }

  // Error state
  if (error || !user) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex justify-between items-center mb-6">
          <button
            onClick={() => navigate('/admin/users')}
            className="flex items-center text-gray-600 hover:text-gray-900"
          >
            <ArrowLeft className="h-5 w-5 mr-1" />
            <span>Back to Users</span>
          </button>
        </div>
        <div className="bg-red-50 border-l-4 border-red-500 p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <XCircle className="h-5 w-5 text-red-400" />
            </div>
            <div className="ml-3">
              <p className="text-sm text-red-700">
                {error || 'User not found'}
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
          <div className="flex items-center">
            <button
              onClick={() => navigate('/admin/users')}
              className="text-gray-600 hover:text-gray-900 mr-4"
            >
              <ArrowLeft className="h-5 w-5" />
            </button>
            <h2 className="text-xl font-semibold text-gray-800">User Details</h2>
          </div>
          <div className="flex space-x-2">
            <Link
              to={`/admin/users/${userId}/edit`}
              className="flex items-center px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
            >
              <Edit className="h-4 w-4 mr-2" />
              Edit User
            </Link>
            <button
              onClick={() => setDeleteUserModalOpen(true)}
              className="flex items-center px-3 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-red-600 hover:bg-red-700"
            >
              <Trash className="h-4 w-4 mr-2" />
              Delete User
            </button>
          </div>
        </div>
        
        <div className="px-6 py-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* User Profile Card */}
            <div className="lg:col-span-1">
              <div className="bg-gray-50 rounded-lg p-6 flex flex-col items-center">
                <div className="w-32 h-32 bg-gray-200 rounded-full overflow-hidden mb-4">
                  {user.userProfile?.temporaryUrl ? (
                    <img
                      src={user.userProfile.temporaryUrl}
                      alt={`${user.first_name} ${user.last_name}`}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full bg-gray-300 flex items-center justify-center">
                      <span className="text-4xl font-semibold text-gray-600">
                        {user.first_name.charAt(0)}{user.last_name.charAt(0)}
                      </span>
                    </div>
                  )}
                </div>
                <h2 className="text-xl font-bold text-gray-900">{user.first_name} {user.last_name}</h2>
                <div className="mt-1 flex space-x-2">
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                    user.role === 'superadmin' ? 'bg-purple-100 text-purple-800' :
                    user.role === 'admin' ? 'bg-blue-100 text-blue-800' :
                    'bg-green-100 text-green-800'
                  }`}>
                    {user.role}
                  </span>
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                    user.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                  }`}>
                    {user.status === 'active' ? (
                      <CheckCircle className="mr-1 h-3 w-3" />
                    ) : (
                      <XCircle className="mr-1 h-3 w-3" />
                    )}
                    {user.status}
                  </span>
                </div>
                
                <div className="mt-6 w-full space-y-4">
                  <div className="flex items-center">
                    <Mail className="h-5 w-5 text-gray-400 mr-3" />
                    <div>
                      <p className="text-xs text-gray-500">Email</p>
                      <p className="text-sm font-medium text-gray-900">{user.email}</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center">
                    <Phone className="h-5 w-5 text-gray-400 mr-3" />
                    <div>
                      <p className="text-xs text-gray-500">Phone</p>
                      <p className="text-sm font-medium text-gray-900">{user.phone_number || 'Not provided'}</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center">
                    <Calendar className="h-5 w-5 text-gray-400 mr-3" />
                    <div>
                      <p className="text-xs text-gray-500">Registered On</p>
                      <p className="text-sm font-medium text-gray-900">{formatDate(user.created_at)}</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center">
                    <Clock className="h-5 w-5 text-gray-400 mr-3" />
                    <div>
                      <p className="text-xs text-gray-500">Last Login</p>
                      <p className="text-sm font-medium text-gray-900">{formatDate(user.last_login)}</p>
                    </div>
                  </div>

                  {/* Referral Code */}
                  {user.referral_code && (
                    <div className="flex items-center">
                      <Share2 className="h-5 w-5 text-gray-400 mr-3" />
                      <div>
                        <p className="text-xs text-gray-500">Referral Code</p>
                        <p className="text-sm font-medium text-gray-900 font-mono">{user.referral_code}</p>
                      </div>
                    </div>
                  )}
                </div>
                
                <div className="mt-6 w-full space-y-3">
                  <button
                    onClick={() => setRoleModalOpen(true)}
                    className="w-full flex justify-center items-center px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                  >
                    <Shield className="h-4 w-4 mr-2" />
                    Change Role
                  </button>
                  
                  <button
                    onClick={() => setStatusModalOpen(true)}
                    className={`w-full flex justify-center items-center px-4 py-2 border rounded-md text-sm font-medium ${
                      user.status === 'active' 
                        ? 'border-red-300 text-red-700 bg-white hover:bg-red-50' 
                        : 'border-green-300 text-green-700 bg-white hover:bg-green-50'
                    }`}
                  >
                    {user.status === 'active' ? (
                      <>
                        <XCircle className="h-4 w-4 mr-2" />
                        Deactivate User
                      </>
                    ) : (
                      <>
                        <CheckCircle className="h-4 w-4 mr-2" />
                        Activate User
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
            
            {/* User Details */}
            <div className="lg:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Plan Information */}
              <div className="bg-white border border-gray-200 rounded-lg p-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                  <Tag className="h-5 w-5 text-blue-500 mr-2" />
                  Subscription Plan
                </h3>
                
                <div className="space-y-4">
                  <div>
                    <p className="text-xs text-gray-500">Plan ID</p>
                    <p className="text-sm font-medium text-gray-900">{(user.plan?.plan_id == 1?'Basic':user.plan?.plan_id == 2?'Gold':'Premium') || 'No plan'}</p>
                  </div>
                  
                  <div>
                    <p className="text-xs text-gray-500">Status</p>
                    <p className="text-sm font-medium">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        user.plan?.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                      }`}>
                        {user.plan?.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </p>
                  </div>
                  
                  <div>
                    <p className="text-xs text-gray-500">Expiration Date</p>
                    <p className="text-sm font-medium text-gray-900">
                      {user.plan?.expiration_date ? formatDate(user.plan.expiration_date) : 'N/A'}
                    </p>
                  </div>
                </div>
              </div>
              
              {/* Storage Information */}
              <div className="bg-white border border-gray-200 rounded-lg p-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                  <Database className="h-5 w-5 text-blue-500 mr-2" />
                  Storage Limits
                </h3>
                
                <div className="space-y-4">
                  <div>
                    <div className="flex justify-between items-center mb-1">
                      <p className="text-xs text-gray-500">Upload Limit</p>
                      <p className="text-xs font-medium text-gray-900">{formatFileSize(user.max_upload_limit_mb)}</p>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div 
                        className="bg-blue-600 h-2 rounded-full"
                        style={{ 
                          width: `${Math.min(
                            (media.reduce((total, item) => total + (item.file_size || 0), 0) / 
                            (user.max_upload_limit_mb * 1024 * 1024)) * 100, 100
                          )}%` 
                        }}
                      ></div>
                    </div>
                    <div className="text-xs text-gray-500 mt-1">
                      {formatFileSize(media.reduce((total, item) => total + (item.file_size || 0), 0))} used
                    </div>
                  </div>
                  
                  <div>
                    <div className="flex justify-between items-center mb-1">
                      <p className="text-xs text-gray-500">Recipients Limit</p>
                      <p className="text-xs font-medium text-gray-900">{user.no_of_recipients}</p>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div 
                        className="bg-green-600 h-2 rounded-full" 
                        style={{ width: `${Math.min((recipients.length / user.no_of_recipients) * 100, 100)}%` }}
                      ></div>
                    </div>
                    <div className="text-xs text-gray-500 mt-1">
                      {recipients.length} recipients used
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Media Stats */}
              <div className="bg-white border border-gray-200 rounded-lg p-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                  <ImageIcon className="h-5 w-5 text-blue-500 mr-2" />
                  Media Stats
                </h3>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-gray-50 rounded-lg p-4 text-center">
                    <div className="text-2xl font-bold text-blue-600">{media.length}</div>
                    <div className="text-sm text-gray-500">Total Files</div>
                  </div>
                  
                  <div className="bg-gray-50 rounded-lg p-4 text-center">
                    <div className="text-2xl font-bold text-green-600">
                      {formatFileSize(media.reduce((total, item) => total + (item.file_size || 0), 0))}
                    </div>
                    <div className="text-sm text-gray-500">Storage Used</div>
                  </div>
                  
                  <div className="bg-gray-50 rounded-lg p-4 text-center">
                    <div className="text-2xl font-bold text-purple-600">
                      {media.filter(item => item.file_type === 'image').length}
                    </div>
                    <div className="text-sm text-gray-500">Images</div>
                  </div>
                  
                  <div className="bg-gray-50 rounded-lg p-4 text-center">
                    <div className="text-2xl font-bold text-orange-600">
                      {media.filter(item => item.file_type === 'video').length}
                    </div>
                    <div className="text-sm text-gray-500">Videos</div>
                  </div>
                </div>
              </div>
              
              {/* Referral Stats */}
              <div className="bg-white border border-gray-200 rounded-lg p-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                  <UserPlus className="h-5 w-5 text-blue-500 mr-2" />
                  Referral Stats
                </h3>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-gray-50 rounded-lg p-4 text-center">
                    <div className="text-2xl font-bold text-indigo-600">{referrals.length}</div>
                    <div className="text-sm text-gray-500">Total Referrals</div>
                  </div>
                  
                  <div className="bg-gray-50 rounded-lg p-4 text-center">
                    <div className="text-2xl font-bold text-teal-600">
                      {referrals.filter(ref => ref.status === 'active').length}
                    </div>
                    <div className="text-sm text-gray-500">Active Referrals</div>
                  </div>
                  
                  <div className="col-span-2 mt-2">
                    <div className="flex justify-between items-center mb-1">
                      <p className="text-xs text-gray-500">Activity Rate</p>
                      <p className="text-xs font-medium text-gray-900">
                        {referrals.length 
                          ? Math.round((referrals.filter(ref => ref.status === 'active').length / referrals.length) * 100) 
                          : 0}%
                      </p>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div 
                        className="bg-indigo-600 h-2 rounded-full" 
                        style={{ 
                          width: referrals.length 
                            ? `${(referrals.filter(ref => ref.status === 'active').length / referrals.length) * 100}%` 
                            : '0%' 
                        }}
                      ></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Referrals Section */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center cursor-pointer"
          onClick={() => setReferralsExpanded(!referralsExpanded)}>
          <h2 className="text-lg font-semibold text-gray-800 flex items-center">
            <UserPlus className="h-5 w-5 text-blue-500 mr-2" />
            Referrals ({referrals.length})
          </h2>
          <button className="text-gray-500 hover:text-gray-700">
            {referralsExpanded ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
          </button>
        </div>
        
        {referralsExpanded && (
          <div className="px-6 py-4">
            {referrals.length === 0 ? (
              <div className="py-6 text-center text-gray-500">
                <UserPlus className="h-12 w-12 text-gray-400 mx-auto mb-2" />
                <p>This user hasn't referred anyone yet.</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        User
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Email
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Joined
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {referrals.map((referral) => (
                      <tr key={referral.id}>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="flex-shrink-0 h-10 w-10">
                              {referral.userProfile?.image_url ? (
                                <img
                                  className="h-10 w-10 rounded-full object-cover"
                                  src={referral.userProfile.image_url}
                                  alt={`${referral.first_name} ${referral.last_name}`}
                                />
                              ) : (
                                <div className="h-10 w-10 rounded-full bg-gray-200 flex items-center justify-center">
                                  <span className="text-gray-600 font-medium">
                                    {referral.first_name.charAt(0)}{referral.last_name.charAt(0)}
                                  </span>
                                </div>
                              )}
                            </div>
                            <div className="ml-4">
                              <div className="text-sm font-medium text-gray-900">
                                {referral.first_name} {referral.last_name}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">{referral.email}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {formatDate(referral.created_at)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            referral.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                          }`}>
                            {referral.status === 'active' ? (
                              <CheckCircle className="mr-1 h-3 w-3" />
                            ) : (
                              <XCircle className="mr-1 h-3 w-3" />
                            )}
                            {referral.status}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <Link
                            to={`/admin/users/${referral.id}`}
                            className="text-indigo-600 hover:text-indigo-900"
                          >
                            <span className="mr-2">View</span>
                            <ExternalLink className="h-4 w-4 inline" />
                          </Link>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>
      
      {/* Media Section */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center cursor-pointer"
          onClick={() => setMediaExpanded(!mediaExpanded)}>
          <h2 className="text-lg font-semibold text-gray-800 flex items-center">
            <ImageIcon className="h-5 w-5 text-blue-500 mr-2" />
            User Media ({media.length})
          </h2>
          <button className="text-gray-500 hover:text-gray-700">
            {mediaExpanded ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
          </button>
        </div>{mediaExpanded && (
          <div className="px-6 py-4">
            {media.length === 0 ? (
              <div className="py-6 text-center text-gray-500">
                <FileText className="h-12 w-12 text-gray-400 mx-auto mb-2" />
                <p>This user hasn't uploaded any media yet.</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {media.map((item) => (
                  <div key={item.id} className="border border-gray-200 rounded-lg overflow-hidden">
                    <div className="bg-gray-50 h-40 flex items-center justify-center">
                      {item.file_type === 'image' ? (
                        <img
                          src={item.temporaryUrl}
                          alt={item.file_name}
                          className="max-h-full max-w-full object-contain"
                        />
                      ) : (
                        <div className="text-center">
                          {getMediaIcon(item.file_type)}
                          <p className="mt-2 text-sm text-gray-500">{item.file_type}</p>
                        </div>
                      )}
                    </div>
                    <div className="p-4">
                      <h4 className="text-sm font-medium text-gray-900 truncate" title={item.file_name}>
                        {item.file_name}
                      </h4>
                      <div className="flex justify-between items-center mt-2">
                        <span className="text-xs text-gray-500">{formatFileSize(item.file_size)}</span>
                        <span className="text-xs text-gray-500">{formatDate(item.created_at)}</span>
                      </div>
                      <div className="flex justify-between items-center mt-3">
                        <div className="flex space-x-2">
                          <button
                            onClick={() => {
                              setSelectedMedia(item);
                              setViewMediaModalOpen(true);
                            }}
                            className="p-1 text-gray-500 hover:text-blue-500"
                            title="View"
                          >
                            <Eye className="h-4 w-4" />
                          </button>
                          <a
                            href={item.temporaryUrl}
                            download={item.file_name}
                            className="p-1 text-gray-500 hover:text-green-500"
                            title="Download"
                          >
                            <Download className="h-4 w-4" />
                          </a>
                          <button
                            onClick={() => {
                              setSelectedMedia(item);
                              setDeleteMediaModalOpen(true);
                            }}
                            className="p-1 text-gray-500 hover:text-red-500"
                            title="Delete"
                          >
                            <Trash className="h-4 w-4" />
                          </button>
                        </div>
                        {item.is_legacy === 1 && (
                          <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800">
                            Legacy
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
      
      {/* Recipients Section */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center cursor-pointer"
          onClick={() => setRecipientsExpanded(!recipientsExpanded)}>
          <h2 className="text-lg font-semibold text-gray-800 flex items-center">
            <UsersIcon className="h-5 w-5 text-blue-500 mr-2" />
            Recipients ({recipients.length})
          </h2>
          <button className="text-gray-500 hover:text-gray-700">
            {recipientsExpanded ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
          </button>
        </div>
        
        {recipientsExpanded && (
          <div className="px-6 py-4">
            {recipients.length === 0 ? (
              <div className="py-6 text-center text-gray-500">
                <UsersIcon className="h-12 w-12 text-gray-400 mx-auto mb-2" />
                <p>This user hasn't added any recipients yet.</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Recipient
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Contact
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Group
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {recipients.map((recipient) => (
                      <tr key={recipient.id}>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="flex-shrink-0 h-10 w-10">
                              {recipient.temporaryUrl ? (
                                <img
                                  className="h-10 w-10 rounded-full object-cover"
                                  src={recipient.temporaryUrl}
                                  alt={`${recipient.first_name} ${recipient.last_name}`}
                                />
                              ) : (
                                <div className="h-10 w-10 rounded-full bg-gray-200 flex items-center justify-center">
                                  <span className="text-gray-600 font-medium">
                                    {recipient.first_name.charAt(0)}{recipient.last_name.charAt(0)}
                                  </span>
                                </div>
                              )}
                            </div>
                            <div className="ml-4">
                              <div className="text-sm font-medium text-gray-900">
                                {recipient.first_name} {recipient.last_name}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">{recipient.email}</div>
                          {recipient.phone && (
                            <div className="text-sm text-gray-500">{recipient.phone}</div>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                            {recipient?.group?.name}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>
      
      {/* Transactions Section */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center cursor-pointer"
          onClick={() => setTransactionsExpanded(!transactionsExpanded)}>
          <h2 className="text-lg font-semibold text-gray-800 flex items-center">
            <Database className="h-5 w-5 text-blue-500 mr-2" />
            Transactions
          </h2>
          <button className="text-gray-500 hover:text-gray-700">
            {transactionsExpanded ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
          </button>
        </div>
        
        {transactionsExpanded && (
          <div className="px-6 py-4">
            {transactions.length === 0 ? (
              <div className="py-6 text-center text-gray-500">
                <Database className="h-12 w-12 text-gray-400 mx-auto mb-2" />
                <p>No transaction history available.</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Date
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Type
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Amount
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Description
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {transactions.map((transaction) => (
                      <tr key={transaction.id}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {formatDate(transaction.created_at)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                            {transaction.type}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {formatAmount(transaction.amount, transaction.currency)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            transaction.status === 'completed' ? 'bg-green-100 text-green-800' :
                            transaction.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                            'bg-red-100 text-red-800'
                          }`}>
                            {transaction.status}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {transaction.description || '-'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>
      
      {/* Activity Section */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center cursor-pointer"
          onClick={() => setActivityExpanded(!activityExpanded)}>
          <h2 className="text-lg font-semibold text-gray-800 flex items-center">
            <Clock className="h-5 w-5 text-blue-500 mr-2" />
            Recent Activity
          </h2>
          <button className="text-gray-500 hover:text-gray-700">
            {activityExpanded ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
          </button>
        </div>
        
        {activityExpanded && (
          <div className="px-6 py-4">
            {activityLogs.length === 0 ? (
              <div className="py-6 text-center text-gray-500">
                <Clock className="h-12 w-12 text-gray-400 mx-auto mb-2" />
                <p>No recent activity available.</p>
              </div>
            ) : (
              <div className="relative">
                <div className="border-l-2 border-gray-200 ml-4">
                  {activityLogs.map((log, index) => (
                    <div key={index} className="relative mb-6">
                      <div className="absolute -left-4 mt-0.5 w-6 h-6 rounded-full bg-white border-2 border-blue-500 flex items-center justify-center">
                        <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                      </div>
                      <div className="ml-6">
                        <div className="text-sm font-medium text-gray-900">{log.type}</div>
                        <div className="flex text-xs text-gray-500 mt-1">
                          <span>{formatDate(log.timestamp)}</span>
                          <span className="mx-2">•</span>
                          <span>{log.ip_address}</span>
                          <span className="mx-2">•</span>
                          <span>{log.device}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
      
      {/* Modals */}
      {user && (
        <>
          <UserStatusModal
            isOpen={statusModalOpen}
            onClose={() => setStatusModalOpen(false)}
            onConfirm={handleToggleStatus}
            user={user}
          />
          
          <UserRoleModal
            isOpen={roleModalOpen}
            onClose={() => setRoleModalOpen(false)}
            onConfirm={handleChangeRole}
            user={user}
          />
          
          <UserDeleteConfirmModal
            isOpen={deleteUserModalOpen}
            onClose={() => setDeleteUserModalOpen(false)}
            onConfirm={handleDeleteUser}
            user={user}
          />
        </>
      )}
      
      {selectedMedia && (
        <>
          <MediaViewModal
            isOpen={viewMediaModalOpen}
            onClose={() => setViewMediaModalOpen(false)}
            media={selectedMedia}
          />
          
          <MediaDeleteConfirmModal
            isOpen={deleteMediaModalOpen}
            onClose={() => setDeleteMediaModalOpen(false)}
            onConfirm={() => handleDeleteMedia(selectedMedia.id)}
            media={selectedMedia}
          />
        </>
      )}
    </div>
  );
};

export default UserDetails;